<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ParceirosAPI from 'dashboard/api/fandata/parceiros';
import PendenciaAPI from 'dashboard/api/fandata/pendencia';
import TaxonomiaAPI from 'dashboard/api/fandata/taxonomia';

// T-02: marcar pendência — o agente escolhe um parceiro (interno/externo) e uma nota opcional.
const props = defineProps({
  show: { type: Boolean, default: false },
  conversationDisplayId: { type: [Number, String], required: true },
});
const emit = defineEmits(['update:show', 'marcada']);

const { t } = useI18n();
const parceiros = reactive({ interna: [], externa: [] });
const form = reactive({ parceiroSlug: '', nota: '' });
const saving = ref(false);
// Taxonomia oficial (fase 2): a planilha do clube diz quem resolve esta causa raiz e em
// quantos dias. Vem como SUGESTÃO pré-selecionada — o agente confirma em vez de decidir.
const sugestao = ref(null);

const isValid = computed(() => !!form.parceiroSlug);

const load = async () => {
  try {
    const { data } = await ParceirosAPI.get();
    parceiros.interna = data.interna || [];
    parceiros.externa = data.externa || [];
  } catch (error) {
    useAlert(t('CONVERSATION.PENDENCIA.LOAD_ERROR'));
  }
  carregarSugestao();
};

// Silencioso de propósito: sem taxonomia importada, ou sem causa raiz que case, o modal
// segue funcionando como antes. A sugestão é um atalho, não um pré-requisito.
const carregarSugestao = async () => {
  try {
    const { data } = await TaxonomiaAPI.sugestao(props.conversationDisplayId);
    const lista = data.sugestoes || [];
    // A taxonomia v1.0 não traz mais RESPONSÁVEL, então nenhuma sugestão vem com parceiro.
    // Exigir parceiro aqui apagaria também a causa raiz e o prazo, que seguem úteis: o
    // agente escolhe a pasta e continua vendo o que a planilha diz daquele caso.
    const melhor = lista.find(s => s.parceiro) || lista[0];
    if (!melhor) return;
    sugestao.value = melhor;
    if (!form.parceiroSlug && melhor.parceiro) form.parceiroSlug = melhor.parceiro.slug;
  } catch (error) {
    sugestao.value = null;
  }
};

const close = () => emit('update:show', false);

const submit = async () => {
  if (!isValid.value) return;
  saving.value = true;
  try {
    await PendenciaAPI.marcar(props.conversationDisplayId, {
      parceiroSlug: form.parceiroSlug,
      nota: form.nota.trim(),
    });
    useAlert(t('CONVERSATION.PENDENCIA.MARCADA'));
    emit('marcada');
    close();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CONVERSATION.PENDENCIA.ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <woot-modal :show="show" :on-close="close">
    <div class="flex flex-col gap-4 p-8">
      <div class="flex flex-col gap-1">
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('CONVERSATION.PENDENCIA.TITLE') }}
        </h2>
        <p class="text-xs text-n-slate-11 leading-snug">
          {{ t('CONVERSATION.PENDENCIA.HELP') }}
        </p>
      </div>

      <div
        v-if="sugestao"
        class="flex flex-col gap-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
      >
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.PENDENCIA.SUGESTAO_TITULO') }}
        </span>
        <span class="text-sm text-n-slate-12">{{ sugestao.causa_raiz }}</span>
        <span class="text-xs text-n-slate-11">
          <template v-if="sugestao.parceiro">{{ sugestao.parceiro.nome }} · </template>
          <!-- O prazo que interessa aqui é o de RESOLUÇÃO, que vem da causa raiz. O inicial da
               planilha v1.0 é "1 dia" em todas as 202 linhas, então mostrá-lo seria repetir a
               mesma frase em todo caso. Sem prazo da causa, cai no que o protocolo diz. -->
          {{
            t('CONVERSATION.PENDENCIA.SUGESTAO_PRAZO', {
              dias: sugestao.sla_final_dias || sugestao.sla_dias,
            })
          }}
        </span>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.PENDENCIA.PARCEIRO_LABEL') }}
        </span>
        <select
          v-model="form.parceiroSlug"
          class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        >
          <option value="" disabled>
            {{ t('CONVERSATION.PENDENCIA.PARCEIRO_PLACEHOLDER') }}
          </option>
          <optgroup
            v-if="parceiros.interna.length"
            :label="t('CONVERSATION.PENDENCIA.INTERNA')"
          >
            <option v-for="p in parceiros.interna" :key="p.slug" :value="p.slug">
              {{ p.nome }}
            </option>
          </optgroup>
          <optgroup
            v-if="parceiros.externa.length"
            :label="t('CONVERSATION.PENDENCIA.EXTERNA')"
          >
            <option v-for="p in parceiros.externa" :key="p.slug" :value="p.slug">
              {{ p.nome }}
            </option>
          </optgroup>
        </select>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CONVERSATION.PENDENCIA.NOTA_LABEL') }}
        </span>
        <textarea
          v-model="form.nota"
          rows="3"
          :placeholder="t('CONVERSATION.PENDENCIA.NOTA_PLACEHOLDER')"
          class="w-full resize-y rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
        />
      </label>

      <div class="flex justify-end gap-3 mt-2">
        <Button
          :label="t('CONVERSATION.PENDENCIA.CANCEL')"
          variant="faded"
          color="slate"
          size="sm"
          @click="close"
        />
        <Button
          :label="t('CONVERSATION.PENDENCIA.SUBMIT')"
          size="sm"
          :disabled="!isValid"
          :is-loading="saving"
          @click="submit"
        />
      </div>
    </div>
  </woot-modal>
</template>
